'use client'

import { ArrowUpRight } from 'lucide-react'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'

interface ArrowButtonProps {
  href: string
  className?: string
  size?: 'sm' | 'md' | 'lg'
  color?: 'brand' | 'white' | 'black'
  groupHover?: boolean
}

export default function ArrowButton({
  href,
  className = '',
  size = 'md',
  color = 'brand',
  groupHover = false,
}: ArrowButtonProps) {
  // Determine if it's an external link
  const isExternal = href.startsWith('http') || href.startsWith('//')

  // Set initial rotation and hover rotation based on type
  const initialRotation = 45 // Base rotation
  const hoverRotation = isExternal ? initialRotation - 45 : initialRotation + 45 // Up for external, down for internal

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
  }

  const colorClasses = {
    brand: 'bg-brand',
    white: 'bg-white',
    black: 'bg-black',
  }

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
  }

  const iconColors = {
    brand: 'text-white',
    white: 'text-black',
    black: 'text-white',
  }

  return (
    <Link
      href={href}
      className={cn(
        sizeClasses[size],
        colorClasses[color],
        'flex items-center justify-center cursor-pointer',
        className,
      )}
    >
      <motion.div
        initial={{ rotate: initialRotation }}
        whileHover={{ rotate: hoverRotation }}
        animate={groupHover ? { rotate: hoverRotation } : { rotate: initialRotation }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        className="w-full h-full flex items-center justify-center"
      >
        <ArrowUpRight className={cn(iconSizes[size], iconColors[color])} />
      </motion.div>
    </Link>
  )
}
